/**
 * src/components/agent/ConversationContextMenu.jsx — Conversation row menu.
 *
 * Phase 82-03 — Chat System Unification, sidebar context menu.
 *
 * Four actions:
 *   - Rename          → flips the row to inline-edit mode (handled by parent)
 *   - Move to project → nested <select> picker
 *   - Archive         → soft-archive (archived_at = now()); becomes Restore
 *                       once the row already has archived_at set
 *   - Delete          → disabled unless archived_at is set AND > 30 days old.
 *                       When enabled, opens confirm sub-panel.
 *
 * Caller owns the menuState { x, y, conversation }. Positioning is clamped to
 * the viewport. Dismiss-on-outside-click is wired by the caller (hook-free so
 * the menu can live inside drawers, modals, etc.).
 *
 * All colors are CSS vars — no raw hex per HARD DEPLOYMENT RULES.
 */

import { useEffect, useRef, useState } from 'react';
import { Edit3, FolderInput, Archive as ArchiveIcon, RotateCcw, Trash2 } from 'lucide-react';
import { useT } from '../../lib/i18n/I18nContext';

const DELETE_ELIGIBLE_AFTER_MS = 30 * 24 * 60 * 60 * 1000;

export function canDelete(conversation, now = Date.now()) {
  if (!conversation?.archived_at) return false;
  const archivedMs = new Date(conversation.archived_at).getTime();
  if (Number.isNaN(archivedMs)) return false;
  return now - archivedMs >= DELETE_ELIGIBLE_AFTER_MS;
}

const menuStyle = {
  position: 'fixed',
  zIndex: 1200,
  minWidth: 220,
  maxWidth: 280,
  background: 'var(--card, var(--bg))',
  borderRadius: 10,
  border: '1px solid var(--bdr)',
  boxShadow: 'var(--shc)',
  padding: '6px 0',
  overflow: 'hidden',
};

const itemStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  width: '100%',
  minHeight: 40,
  padding: '6px 14px',
  fontSize: 13,
  fontWeight: 500,
  color: 'var(--t1)',
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  fontFamily: 'inherit',
  textAlign: 'left',
};

const disabledItemStyle = {
  ...itemStyle,
  color: 'var(--tm)',
  cursor: 'not-allowed',
  opacity: 0.6,
};

const dangerItemStyle = {
  ...itemStyle,
  color: 'var(--bad)',
};

const dividerStyle = {
  height: 1,
  background: 'var(--bdr)',
  margin: '4px 0',
};

const panelStyle = {
  padding: '10px 14px',
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
};

const panelLabelStyle = {
  fontSize: 13,
  fontWeight: 500,
  color: 'var(--t1)',
  lineHeight: 1.4,
};

const panelSubStyle = {
  fontSize: 12,
  color: 'var(--tm)',
  lineHeight: 1.4,
};

const btnRowStyle = {
  display: 'flex',
  gap: 8,
  justifyContent: 'flex-end',
};

const cancelBtnStyle = {
  minHeight: 36,
  padding: '6px 14px',
  borderRadius: 8,
  border: 'none',
  background: 'var(--glass)',
  color: 'var(--t1)',
  fontSize: 13,
  fontWeight: 500,
  cursor: 'pointer',
  fontFamily: 'inherit',
};

const dangerBtnStyle = {
  minHeight: 36,
  padding: '6px 14px',
  borderRadius: 8,
  border: 'none',
  background: 'var(--bad)',
  color: 'var(--on-navy, #FFF)',
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
  fontFamily: 'inherit',
};

const selectStyle = {
  width: '100%',
  minHeight: 40,
  padding: '6px 10px',
  fontSize: 13,
  borderRadius: 8,
  border: '1px solid var(--bdr)',
  background: 'var(--bg, var(--glass))',
  color: 'var(--t1)',
  outline: 'none',
  fontFamily: 'inherit',
  cursor: 'pointer',
};

function hoverOn(e) {
  if (e.currentTarget.dataset.disabled === 'true') return;
  e.currentTarget.style.background = 'var(--glass)';
}
function hoverOff(e) {
  e.currentTarget.style.background = 'transparent';
}

function ProjectPicker({ projects, currentProjectId, onCommit, onCancel }) {
  const { t } = useT();
  const sorted = [...(projects || [])].sort((a, b) => {
    const aName = a.project_name || a.display_name || '';
    const bName = b.project_name || b.display_name || '';
    return aName.localeCompare(bName);
  });

  return (
    <div style={panelStyle}>
      <label style={panelLabelStyle} htmlFor="cvm-project-picker">{t('agentChat.moveToProject')}</label>
      <select
        id="cvm-project-picker"
        autoFocus
        defaultValue={currentProjectId || ''}
        onChange={e => onCommit(e.target.value || null)}
        style={selectStyle}
        data-testid="cvm-project-picker"
      >
        <option value="">— Unassigned —</option>
        {sorted.map(p => (
          <option key={p.id} value={p.id}>
            {p.project_name || p.display_name || 'Unnamed'}
          </option>
        ))}
      </select>
      <div style={btnRowStyle}>
        <button type="button" style={cancelBtnStyle} onClick={onCancel} data-testid="cvm-project-cancel">
          Cancel
        </button>
      </div>
    </div>
  );
}

function DeleteConfirm({ title, onConfirm, onCancel }) {
  return (
    <div style={panelStyle}>
      <span style={panelLabelStyle}>Delete &quot;{title || 'Untitled'}&quot;?</span>
      <span style={panelSubStyle}>
        Archived more than 30 days ago. This removes the chat from your history.
      </span>
      <div style={btnRowStyle}>
        <button type="button" style={cancelBtnStyle} onClick={onCancel} data-testid="cvm-delete-cancel">
          Cancel
        </button>
        <button type="button" style={dangerBtnStyle} onClick={onConfirm} data-testid="cvm-delete-confirm">
          Delete
        </button>
      </div>
    </div>
  );
}

export default function ConversationContextMenu({
  menuState,
  projects = [],
  onClose,
  onRename,
  onMoveToProject,
  onArchive,
  onRestore,
  onDelete,
}) {
  const [subView, setSubView] = useState(null); // null | 'project' | 'delete'
  const menuRef = useRef(null);

  useEffect(() => {
    setSubView(null);
  }, [menuState?.conversation?.id]);

  useEffect(() => {
    if (!menuState) return undefined;
    const handleClick = e => {
      if (menuRef.current && menuRef.current.contains(e.target)) return;
      onClose?.();
    };
    const handleKey = e => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [menuState, onClose]);

  if (!menuState) return null;

  const { x, y, conversation } = menuState;
  if (!conversation) return null;

  const isArchived = Boolean(conversation.archived_at);
  const deleteAllowed = canDelete(conversation);

  const menuWidth = 240;
  const menuHeight = subView === 'project' ? 180 : subView === 'delete' ? 160 : 220;
  const clampedX = Math.max(8, Math.min(x, (typeof window !== 'undefined' ? window.innerWidth : 800) - menuWidth - 8));
  const clampedY = Math.max(8, Math.min(y, (typeof window !== 'undefined' ? window.innerHeight : 600) - menuHeight - 8));

  return (
    <div
      ref={menuRef}
      role="menu"
      style={{ ...menuStyle, left: clampedX, top: clampedY }}
      data-testid="conversation-context-menu"
    >
      {!subView && (
        <>
          <button
            type="button"
            role="menuitem"
            style={itemStyle}
            onMouseEnter={hoverOn}
            onMouseLeave={hoverOff}
            onClick={() => { onRename?.(conversation); onClose?.(); }}
            data-testid="cvm-rename"
          >
            <Edit3 size={14} color="var(--tm)" />
            Rename
          </button>

          <button
            type="button"
            role="menuitem"
            style={itemStyle}
            onMouseEnter={hoverOn}
            onMouseLeave={hoverOff}
            onClick={() => setSubView('project')}
            data-testid="cvm-move"
          >
            <FolderInput size={14} color="var(--tm)" />
            Move to project
          </button>

          <div style={dividerStyle} />

          {!isArchived && (
            <button
              type="button"
              role="menuitem"
              style={itemStyle}
              onMouseEnter={hoverOn}
              onMouseLeave={hoverOff}
              onClick={() => { onArchive?.(conversation); onClose?.(); }}
              data-testid="cvm-archive"
            >
              <ArchiveIcon size={14} color="var(--tm)" />
              Archive
            </button>
          )}

          {isArchived && (
            <button
              type="button"
              role="menuitem"
              style={itemStyle}
              onMouseEnter={hoverOn}
              onMouseLeave={hoverOff}
              onClick={() => { onRestore?.(conversation); onClose?.(); }}
              data-testid="cvm-restore"
            >
              <RotateCcw size={14} color="var(--tm)" />
              Restore
            </button>
          )}

          <button
            type="button"
            role="menuitem"
            aria-disabled={!deleteAllowed}
            data-disabled={!deleteAllowed}
            style={deleteAllowed ? dangerItemStyle : disabledItemStyle}
            onMouseEnter={hoverOn}
            onMouseLeave={hoverOff}
            onClick={() => { if (deleteAllowed) setSubView('delete'); }}
            title={
              !isArchived
                ? 'Archive first'
                : !deleteAllowed
                  ? 'Available after 30 days in archive'
                  : undefined
            }
            data-testid="cvm-delete"
          >
            <Trash2 size={14} color={deleteAllowed ? 'var(--bad)' : 'var(--tm)'} />
            Delete
          </button>
        </>
      )}

      {subView === 'project' && (
        <ProjectPicker
          projects={projects}
          currentProjectId={conversation.project_id}
          onCommit={pid => { onMoveToProject?.(conversation, pid); onClose?.(); }}
          onCancel={() => setSubView(null)}
        />
      )}

      {subView === 'delete' && (
        <DeleteConfirm
          title={conversation.title}
          onConfirm={() => { onDelete?.(conversation); onClose?.(); }}
          onCancel={() => setSubView(null)}
        />
      )}
    </div>
  );
}
